import { useState, useEffect, useCallback } from 'react';
import { fetchPatientReviews, resolvePatientReview, previewPatientMerge } from '../services/api';
import { Skeleton } from '../components/Skeleton';
import './Admin.css';
import './PatientReviews.css';

// Admin queue for possible duplicate patients raised by facility sync.
//
// Sync never merges on its own. When matching is ambiguous it creates a patient
// and asks a human here; when it links on demographics alone it asks a human to
// confirm or split. "Link" is a real merge — preview, required reason, confirm —
// and the item closes only if the merge succeeds.

const REASON_LABELS = {
  multiple_candidates: 'Several possible matches',
  archived_match: 'Matched an archived patient',
  demographic_link: 'Linked on name + DOB + sex',
  mrn_conflict: 'Match already has a different MRN',
  partial_match: 'Partial match',
};

const REASON_HELP = {
  multiple_candidates:
    'More than one existing patient matched exactly, so a new patient was created. Link it to the right one, or keep it separate.',
  mrn_conflict:
    'One existing patient matched on name, date of birth and sex, but already has a different MRN at this facility, so a new patient was created. Link them only if the two MRNs are the same person.',
  archived_match_identifier:
    "This source's identifier belongs to an archived patient. The visit was attached to that patient; it was not restored.",
  archived_match_demographic:
    'The only match is an archived patient, so a new patient was created instead. To link them, restore the archived patient first.',
  demographic_link:
    'Linked to an existing patient on name, date of birth and sex. Confirm it is the same person, or split it into a new patient.',
};

const ACTION_LABELS = {
  link: 'Link (merge)…',
  keep: 'Keep separate',
  restore: 'Restore patient',
  confirm: 'Confirm link',
  split: 'Split into new patient',
  dismiss: 'Dismiss',
};

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'resolved_linked', label: 'Linked' },
  { value: 'resolved_kept', label: 'Kept' },
  { value: 'resolved_restored', label: 'Restored' },
  { value: 'resolved_confirmed', label: 'Confirmed' },
  { value: 'resolved_split', label: 'Split' },
  { value: 'dismissed', label: 'Dismissed' },
];

// Wire field → label, in display order. Matches the sync contract's patient object.
const DEMOGRAPHIC_FIELDS = [
  ['first_name', 'First name'],
  ['last_name', 'Last name'],
  ['date_of_birth', 'Date of birth'],
  ['sex', 'Sex'],
  ['address_line1', 'Address'],
  ['address_line2', 'Address 2'],
  ['city', 'City'],
  ['state', 'State'],
  ['postal_code', 'ZIP'],
  ['phone', 'Phone'],
  ['email', 'Email'],
];

const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? d : `${dt.toLocaleDateString()} ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const fullName = (demo) => (demo ? `${demo.first_name || ''} ${demo.last_name || ''}`.trim() || '—' : '—');

const errorText = (err, fallback) => err.response?.data?.error?.message || fallback;

function helpKey(item) {
  if (item.reason === 'archived_match') return `archived_match_${item.archived_match_kind}`;
  return item.reason;
}

function DemographicsCompare({ incoming, incomingMrn, existing, existingLabel }) {
  return (
    <table className="review-compare">
      <thead>
        <tr>
          <th />
          <th>Incoming from source</th>
          <th>{existingLabel}</th>
        </tr>
      </thead>
      <tbody>
        {DEMOGRAPHIC_FIELDS.map(([key, label]) => {
          // A field the source OMITTED is not a difference: confirming leaves the
          // patient's value unchanged. A field sent as null would clear it.
          const sent = !!incoming && Object.prototype.hasOwnProperty.call(incoming, key);
          const a = sent ? incoming[key] ?? null : null;
          const b = existing?.[key] ?? null;
          const differs = sent && existing && String(a ?? '').toLowerCase() !== String(b ?? '').toLowerCase();
          return (
            <tr key={key} className={differs ? 'review-compare-diff' : ''}>
              <th scope="row">{label}</th>
              <td>{sent ? (a ?? <span className="review-muted">—</span>) : <span className="review-muted">not sent</span>}</td>
              <td>{b ?? <span className="review-muted">—</span>}</td>
            </tr>
          );
        })}
        <tr>
          <th scope="row">MRN (this facility)</th>
          <td>{incomingMrn ?? <span className="review-muted">—</span>}</td>
          <td className="review-muted">—</td>
        </tr>
      </tbody>
    </table>
  );
}

function CandidateRow({ candidate, selectable, selected, onSelect }) {
  const demo = candidate.demographics;
  return (
    <label className={`review-candidate ${selected ? 'selected' : ''} ${selectable ? '' : 'static'}`}>
      {selectable && (
        <input type="radio" name="review-candidate" checked={selected} onChange={() => onSelect(candidate.patient_id)} />
      )}
      <span className="review-candidate-name">{fullName(demo)}</span>
      <span className="review-candidate-meta">
        #{candidate.patient_id} · DOB {demo?.date_of_birth || '—'} · {candidate.facility_name || '—'}
      </span>
      {candidate.archived && <span className="review-tag review-tag--archived">Archived</span>}
      {candidate.merged_into_patient_id && (
        <span className="review-tag review-tag--merged">
          Was #{candidate.stored_patient_id}, since merged
        </span>
      )}
    </label>
  );
}

function ReviewCard({ item, onAction, busy }) {
  const subject = item.subject;
  const showCompare = item.reason === 'demographic_link' || item.reason === 'archived_match';

  return (
    <article className="review-card">
      <header className="review-card-head">
        <span className={`review-reason review-reason--${item.reason}`}>{REASON_LABELS[item.reason] || item.reason}</span>
        <span className="review-card-meta">
          {item.facility_name || `Facility ${item.facility_id}`} · {item.source_system} patient{' '}
          <code>{item.source_patient_id}</code> · {fmtDateTime(item.created_at)}
        </span>
      </header>

      <p className="review-help">{REASON_HELP[helpKey(item)]}</p>

      <div className="review-body">
        <section>
          <h3>{item.reason === 'demographic_link' ? 'Linked patient' : 'Patient the visit was attached to'}</h3>
          {subject ? (
            <CandidateRow candidate={{ ...subject, stored_patient_id: subject.patient_id }} selectable={false} />
          ) : (
            <p className="review-muted">Patient not found.</p>
          )}

          {item.reason !== 'demographic_link' && (
            <>
              <h3>{item.candidates.length === 1 ? 'Possible match' : 'Possible matches'}</h3>
              {item.candidates.map((c) => (
                <CandidateRow key={c.stored_patient_id} candidate={c} selectable={false} />
              ))}
            </>
          )}
        </section>

        {showCompare && item.incoming_demographics && (
          <section>
            <DemographicsCompare
              incoming={item.incoming_demographics}
              incomingMrn={item.incoming_mrn}
              existing={subject?.demographics}
              existingLabel={item.reason === 'demographic_link' ? 'Linked patient' : 'Attached patient'}
            />
          </section>
        )}
      </div>

      {item.status === 'open' ? (
        <footer className="review-actions">
          {item.allowed_actions.map((action) => (
            <button
              key={action}
              className={action === 'dismiss' ? 'admin-modal-btn-cancel' : action === 'link' || action === 'split' ? 'admin-confirm-danger' : 'review-btn-primary'}
              disabled={busy}
              onClick={() => onAction(item, action)}
            >
              {ACTION_LABELS[action]}
            </button>
          ))}
        </footer>
      ) : (
        <footer className="review-resolved">
          Resolved {fmtDateTime(item.resolved_at)}
          {item.resolution_note ? <> — <em>{item.resolution_note}</em></> : null}
        </footer>
      )}
    </article>
  );
}

function LinkDialog({ item, onCancel, onDone }) {
  const selectable = item.candidates.filter((c) => c.patient_id !== item.subject?.patient_id);
  const [candidateId, setCandidateId] = useState(selectable.length === 1 ? selectable[0].patient_id : null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!candidateId || !item.subject) return;
    let active = true;
    setLoading(true);
    setPreview(null);
    setPreviewError(null);
    previewPatientMerge(candidateId, item.subject.patient_id)
      .then((res) => { if (active) setPreview(res.data); })
      .catch((err) => { if (active) setPreviewError(errorText(err, 'Could not build the merge preview.')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [candidateId, item.subject]);

  const confirm = async () => {
    setSaving(true);
    setError(null);
    try {
      await resolvePatientReview(item.id, { action: 'link', candidatePatientId: candidateId, reason: reason.trim() });
      onDone('Linked — the patients were merged.');
    } catch (err) {
      setError(errorText(err, 'The merge did not complete. Nothing was changed.'));
    } finally {
      setSaving(false);
    }
  };

  const m = preview?.moves;
  return (
    <div className="admin-modal-overlay">
      <div className="admin-confirm-dialog review-link-dialog">
        <h3>Link by merging patients</h3>
        <p>
          <strong>{fullName(item.subject?.demographics)}</strong> (#{item.subject?.patient_id}) will be merged into the
          patient you choose and archived. This cannot be undone from here.
        </p>

        <div className="review-link-candidates">
          {selectable.map((c) => (
            <CandidateRow key={c.stored_patient_id} candidate={c} selectable selected={candidateId === c.patient_id} onSelect={setCandidateId} />
          ))}
        </div>

        {loading && <div className="merge-preview-loading">Analyzing accounts…</div>}
        {previewError && <div className="admin-modal-alert error">{previewError}</div>}
        {m && (
          <div className="merge-stats">
            <MoveStat label="Claims" value={m.claims} />
            <MoveStat label="Payments" value={m.patient_payments} />
            <MoveStat label="Statements" value={m.patient_statements} />
            <MoveStat label="Payment plans" value={m.payment_plans} />
            <MoveStat label="Facility MRNs" value={m.mrns_moved} />
            <MoveStat label="Coverages" value={m.insurances_moved} />
            <MoveStat label="Source IDs" value={m.source_ids_moved} />
            <MoveStat label="Review items" value={m.reviews_moved} />
          </div>
        )}
        {preview?.warnings?.map((w, i) => <div className="merge-warning" key={i}><span>{w}</span></div>)}

        <label className="review-field-label" htmlFor="review-link-reason">
          Reason for merge <span className="merge-required">*</span>
        </label>
        <textarea
          id="review-link-reason"
          className="merge-reason-input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Same patient — confirmed DOB and address with the front desk."
        />
        {error && <div className="admin-modal-alert error">{error}</div>}

        <div className="admin-confirm-actions">
          <button className="admin-modal-btn-cancel" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="admin-confirm-danger" onClick={confirm} disabled={saving || !preview || !reason.trim()}>
            {saving && <span className="admin-modal-spinner" />}
            {saving ? 'Merging…' : 'Confirm merge'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionDialog({ item, action, onCancel, onDone }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const copy = {
    keep: 'Keep these patients as separate people. Nothing is merged or linked.',
    restore: 'Restore the archived patient so billers can see it again.',
    confirm: 'Confirm this is the same person. The link becomes a manual link.',
    split: 'Create a new patient from the incoming details and move this source identifier (and the MRN it brought) to it. The linked patient is left as it is.',
    dismiss: 'Close this item without changing any patient.',
  }[action];

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await resolvePatientReview(item.id, { action, note: note.trim() || undefined });
      onDone(`${ACTION_LABELS[action]} — done.`);
    } catch (err) {
      setError(errorText(err, 'That did not complete. Nothing was changed.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-overlay">
      <div className="admin-confirm-dialog">
        <h3>{ACTION_LABELS[action]}</h3>
        <p>{copy}</p>
        <label className="review-field-label" htmlFor="review-note">Note (optional)</label>
        <textarea id="review-note" className="merge-reason-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        {error && <div className="admin-modal-alert error">{error}</div>}
        <div className="admin-confirm-actions">
          <button className="admin-modal-btn-cancel" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className={action === 'split' ? 'admin-confirm-danger' : 'review-btn-primary'} onClick={submit} disabled={saving}>
            {saving && <span className="admin-modal-spinner" />}
            {saving ? 'Saving…' : ACTION_LABELS[action]}
          </button>
        </div>
      </div>
    </div>
  );
}

function MoveStat({ label, value }) {
  return (
    <div className={`merge-stat ${value > 0 ? 'has-value' : ''}`}>
      <span className="merge-stat-value">{value ?? 0}</span>
      <span className="merge-stat-label">{label}</span>
    </div>
  );
}

function PatientReviews() {
  const [status, setStatus] = useState('open');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [dialog, setDialog] = useState(null); // { item, action }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchPatientReviews(status);
      setItems(res.data || []);
    } catch (err) {
      setItems([]);
      setMsg({ type: 'error', text: errorText(err, 'Failed to load the review queue.') });
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const done = (text) => {
    setDialog(null);
    setMsg({ type: 'success', text });
    load();
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-text">
          <h1>Patient Reviews</h1>
          <p>Possible duplicate patients from facility sync. Sync never merges on its own — every link here is a reviewed merge.</p>
        </div>
      </div>

      <div className="admin-toolbar">
        <label className="review-status-filter">
          <span>Status</span>
          <select value={status} onChange={(e) => { setMsg(null); setStatus(e.target.value); }}>
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        {!loading && <span className="review-count">{items.length} item{items.length === 1 ? '' : 's'}</span>}
      </div>

      {msg && <div className={`admin-modal-alert ${msg.type}`} style={{ marginBottom: 20 }}><span>{msg.text}</span></div>}

      {loading ? (
        <div className="review-list" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div className="review-card" key={i}>
              <Skeleton className="skeleton-text" style={{ height: 18, width: 260 }} />
              <Skeleton className="skeleton-text" style={{ height: 14, width: '70%', marginTop: 14 }} />
              <Skeleton style={{ height: 80, width: '100%', marginTop: 16, borderRadius: 10 }} />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="admin-empty">
          <h3>Nothing to review</h3>
          <p>{status === 'open' ? 'No possible duplicates are waiting.' : 'No items with this status.'}</p>
        </div>
      ) : (
        <div className="review-list">
          {items.map((item) => (
            <ReviewCard key={item.id} item={item} busy={!!dialog} onAction={(it, action) => { setMsg(null); setDialog({ item: it, action }); }} />
          ))}
        </div>
      )}

      {dialog?.action === 'link' && <LinkDialog item={dialog.item} onCancel={() => setDialog(null)} onDone={done} />}
      {dialog && dialog.action !== 'link' && (
        <ActionDialog item={dialog.item} action={dialog.action} onCancel={() => setDialog(null)} onDone={done} />
      )}
    </div>
  );
}

export default PatientReviews;
